// src/services/WeatherService.js

/**
 * Service to fetch real-time weather data.
 * Uses Open-Meteo API (Free, no API key required for non-commercial use).
 * https://open-meteo.com/
 */

const BASE_URL = 'https://api.open-meteo.com/v1/forecast';

// Default location: Upper Marlboro, MD (Agricultural Hub of DMV)
const DEFAULT_COORDS = {
    latitude: 38.8180,
    longitude: -76.7470
};

export const weatherService = {
    /**
     * Fetch current weather and basic forecast
     * @param {number} lat - Latitude
     * @param {number} lon - Longitude
     */
    async getCurrentWeather(lat = DEFAULT_COORDS.latitude, lon = DEFAULT_COORDS.longitude) {
        try {
            const params = new URLSearchParams({
                latitude: lat,
                longitude: lon,
                current: 'temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,wind_direction_10m',
                daily: 'temperature_2m_max,temperature_2m_min,precipitation_sum',
                timezone: 'auto'
            });

            const response = await fetch(`${BASE_URL}?${params}`);

            if (!response.ok) {
                throw new Error('Weather data fetch failed');
            }

            const data = await response.json();
            return this.formatWeatherData(data);
        } catch (error) {
            console.error('Weather Service Error:', error);
            throw error;
        }
    },

    /**
     * Format the raw API response into a cleaner internal format
     */
    formatWeatherData(data) {
        const current = data.current;

        return {
            temperature: Math.round(current.temperature_2m),
            humidity: current.relative_humidity_2m,
            windSpeed: current.wind_speed_10m,
            windDirection: current.wind_direction_10m,
            condition: this.getWeatherCondition(current.weather_code),
            unit: data.current_units.temperature_2m,
            daily: {
                maxTemp: data.daily.temperature_2m_max[0],
                minTemp: data.daily.temperature_2m_min[0],
                precipitation: data.daily.precipitation_sum[0]
            }
        };
    },

    /**
     * Map WMO Weather Codes to string descriptions
     * https://open-meteo.com/en/docs
     */
    getWeatherCondition(code) {
        // Simplified mapping
        if (code === 0) return 'Clear Sky';
        if (code >= 1 && code <= 3) return 'Partly Cloudy';
        if (code >= 45 && code <= 48) return 'Foggy';
        if (code >= 51 && code <= 55) return 'Drizzle';
        if (code >= 61 && code <= 67) return 'Rain';
        if (code >= 71 && code <= 77) return 'Snow';
        if (code >= 80 && code <= 82) return 'Showers';
        if (code >= 95) return 'Thunderstorm';
        return 'Unknown';
    }
};
